// utils/formatters.js

/* Los símbolos, en UN solo sitio: cada pantalla que se hacía su propio mapa
   acababa con una lista más corta y un «€» de reserva que le salía a un hotel
   de México al poner el precio de su primera habitación. */
export const CURRENCY_SYMBOLS = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  JPY: "¥",
  COP: "$",
  BRL: "R$",
  MXN: "$",
  ARS: "$",
  CAD: "C$",
  AUD: "A$",
  CHF: "CHF",
  CNY: "¥",
  KRW: "₩",
  INR: "₹",
  RUB: "₽",
  CLP: "$",
  PEN: "S/",
  UYU: "$U",
  DOP: "RD$",
  PYG: "₲",
  BOB: "Bs",
  CRC: "₡",
  GTQ: "Q",
  PAB: "B/.",
  MAD: "DH",

  // Norte de Europa. Las cuatro coronas comparten «kr»; el código va al lado
  // en todas las pantallas, así que no se confunden.
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  ISK: "kr",

  // Centro y este de Europa.
  PLN: "zł",
  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  BGN: "лв",
  TRY: "₺",

  // Resto.
  NZD: "NZ$",
  ZAR: "R",
  AED: "د.إ",
  HNL: "L",
  NIO: "C$",
  VES: "Bs.S",
};

/**
 * Las monedas que se ofrecen al crear o editar un alojamiento, en el orden en
 * que salen en el desplegable.
 *
 * Vive aquí y no en cada pantalla porque antes había DOS listas iguales
 * —`PriceProperty.vue` y `SixthStep.vue`— y cualquier moneda nueva había que
 * acordarse de añadirla en las dos.
 */
export const MONEDAS_DISPONIBLES = [
  // Las de siempre
  "EUR", "USD", "GBP", "CHF",
  // Norte de Europa
  "SEK", "NOK", "DKK", "ISK",
  // Centro y este de Europa
  "PLN", "CZK", "HUF", "RON", "BGN", "TRY",
  // América Latina
  "BRL", "MXN", "ARS", "CLP", "COP", "PEN", "UYU", "PYG", "BOB",
  "DOP", "CRC", "GTQ", "PAB", "HNL", "NIO", "VES",
  // Resto del mundo
  "CAD", "AUD", "NZD", "JPY", "CNY", "KRW", "INR", "AED", "MAD", "ZAR",
].map((code) => ({ code, symbol: CURRENCY_SYMBOLS[code] || code }));

/**
 * El símbolo de una moneda. Sin código no se inventa nada: se devuelve lo que
 * se pida por defecto, y si el código no está en la lista se usa el propio
 * código («PLN 120»), que se entiende y nunca miente.
 */
export function currencySymbol(currency, porDefecto = "") {
  const code = String(currency || "").trim().toUpperCase();
  if (!code) return porDefecto;
  return CURRENCY_SYMBOLS[code] || code;
}

export function formatCurrency_global(value, currency = "EUR") {
  const numericValue = Number(value) || 0;

  /* Antes aquí había OTRO mapa, más corto: a un hotel sueco le salía «SEK»
     mientras que en la pantalla de al lado veía «kr». El de arriba es el único. */
  const currencySymbols = CURRENCY_SYMBOLS;

  // Separar la parte entera y decimal
  const parts = numericValue.toFixed(2).split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Agregar puntos cada 3 dígitos desde la derecha
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  // Obtener el símbolo de la moneda
  const symbol = currencySymbols[currency] || currency;

  // Para EUR, el símbolo va al final
  // Para USD, COP, etc., el símbolo va al principio
  if (currency === "EUR") {
    return `${formattedInteger},${decimalPart} ${symbol}`;
  } else if (["USD", "COP", "MXN", "ARS", "CAD", "AUD"].includes(currency)) {
    return `${symbol}${formattedInteger},${decimalPart}`;
  } else if (currency === "BRL") {
    return `${symbol} ${formattedInteger},${decimalPart}`;
  } else {
    // Para otras monedas, usar el símbolo al final
    return `${formattedInteger},${decimalPart} ${symbol}`;
  }
}

// 🚨 «always»: el español de España no pone el punto en números de 4 cifras
// (1539,00 € pero 15.390,00 €), así que una reserva de 1.539 € salía «1539,00 EUR»
// junto a «1.796,25 EUR». Con «always» lleva siempre el separador de su idioma;
// en los demás (es-CO, es-MX, pt-BR, en-US…) no cambia nada, ya lo ponían.
const GROUPING = "always";

// utils/formatters.js
export function formatCurrency(amount, currency = "USD") {
  const num = Number(amount);
  if (isNaN(num)) return amount;

  const locale = getLocaleForCurrency(currency);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: GROUPING,
  }).format(num);
}

// Igual que formatCurrency pero muestra el CÓDIGO de la moneda en letras
// (EUR, BRL, COP…) en lugar del símbolo. Ej: "138,00 EUR".
export function formatCurrencyCode(amount, currency = "USD") {
  const num = Number(amount);
  if (isNaN(num)) return amount;

  const code = String(currency || "USD").toUpperCase();
  const locale = getLocaleForCurrency(code);

  const n = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: GROUPING,
  }).format(num);

  return `${n} ${code}`;
}

export function formatAmount(amount, currency = "USD") {
  const num = Number(amount);
  if (isNaN(num)) return amount;

  // Obtener el locale a partir del currency (ver mapeo más abajo)
  const locale = getLocaleForCurrency(currency);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: GROUPING,
  }).format(num);
}

export function getLocaleForCurrency(currency) {
  const map = {
    USD: "en-US",
    EUR: "es-ES",
    BRL: "pt-BR",
    COP: "es-CO",
    MXN: "es-MX",
    GBP: "en-GB",
    ARS: "es-AR",
    CLP: "es-CL",
    // Agrega más si hace falta
  };
  return map[currency] || "en-US";
}

// ── Moneda por país ────────────────────────────────────────────────────────────
// La columna `currency` de la propiedad NO es fiable (a veces trae "EUR" en
// propiedades de Colombia), por eso la moneda real se resuelve por el país del
// alojamiento (code_pais). Fuente única para toda la UI de reserva/pagos/cargos.
export const COUNTRY_CURRENCY = {
  CO: "COP", MX: "MXN", AR: "ARS", CL: "CLP", PE: "PEN", BR: "BRL",
  US: "USD", GB: "GBP", AE: "AED",
  ES: "EUR", IT: "EUR", FR: "EUR", DE: "EUR", PT: "EUR", NL: "EUR", IE: "EUR",
};

/**
 * Moneda REAL de una reserva: 1) país del alojamiento, 2) moneda de las líneas,
 * 3) respaldo (reserva/propiedad/EUR).
 *
 * 🚨 EL PAÍS VA PRIMERO, y esto viene de dos fallos reales que decían lo contrario:
 *
 *  · Un alojamiento de Málaga salía en LIBRAS porque sus líneas se habían guardado
 *    en GBP. Fiarse de las líneas es fiarse de un dato que puede venir sucio.
 *  · Y «Apartamento 1», en España y con EUR en su propia columna, salía en BRL
 *    porque `plantillas.currency` es un ACCESOR que devuelve la moneda del ANUNCIO
 *    padre, y ese anuncio estaba mal configurado. Así que `p.currency` tampoco es
 *    de fiar.
 *
 * El país sí lo es, y el mapa de arriba ya cubre la zona euro. De ahí este orden.
 *
 * 🚨 Y es UNA sola función a propósito: el panel de pagos usaba esta y la ficha de
 * importes tenía su propia copia con otro orden, así que el MISMO bloque enseñaba
 * «Subtotal 500,00 BRL» arriba y «TOTAL 500,00 EUR» abajo. Dos monedas para el
 * mismo dinero.
 */
export function resolveReservationCurrency(reservationData, storeProperty = null) {
  const rd = reservationData || {};
  const p = rd.propiedad || storeProperty || {};

  const byCountry = COUNTRY_CURRENCY[String(p.code_pais || "").toUpperCase()];
  if (byCountry) return byCountry;

  const lines = Array.isArray(rd.financial_lines)
    ? rd.financial_lines
    : Array.isArray(rd.invoice_charges)
      ? rd.invoice_charges
      : [];
  const lineCcy = lines.find((l) => l && l.currency)?.currency;
  if (lineCcy) return String(lineCcy).toUpperCase();

  return String(rd.currency || p.currency || "EUR").toUpperCase();
}

// Monedas latinas que se muestran SIN decimales (formato peso).
const NO_DECIMALS_CCY = ["COP", "ARS", "CLP", "PEN", "JPY", "KRW"];

// Solo el importe, con los separadores y decimales de su moneda y SIN el código:
// "916.000", "138,00". Para sitios que ya pintan la moneda en su propia etiqueta,
// como la tarjeta de suscripción; ahí concatenar el código otra vez daba "COP COP".
export function formatMoneyNumber(amount, currency = "EUR") {
  const code = String(currency || "EUR").toUpperCase();
  const dec = NO_DECIMALS_CCY.includes(code) ? 0 : 2;
  return (Number(amount) || 0).toLocaleString(getLocaleForCurrency(code), {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
    useGrouping: GROUPING,
  });
}

// Formatea importe + CÓDIGO de moneda: "916.000 COP", "138,00 EUR", "1,200.00 USD".
export function formatMoneyCode(amount, currency = "EUR") {
  const code = String(currency || "EUR").toUpperCase();
  return `${formatMoneyNumber(amount, code)} ${code}`;
}
